// Active-culture store for the frontend i18n layer
// (wiki/feature-resource-generation.md §Localization). The generated resource
// catalogue (`@voicedojo/backend-types`, `<Domain>Values`) carries a `default`
// (English) value map plus a `vi-VN` map; this store names which of those the
// `t()` resolver reads, with `default` as the fallback culture.
//
// Lives in `.svelte.ts` so the `$state` rune is available; the getter export
// keeps reads reactive in both runes-mode and Svelte-4-mode hosts. The choice
// is persisted in localStorage so it survives reloads, and applied to the
// document root's `lang` attribute for correct hyphenation / screen-reader
// pronunciation.

import { browser } from '$app/environment';
import { DefaultCulture } from '@voicedojo/backend-types';

/** The cultures the generated catalogue ships values for. `default` is English. */
export type Culture = 'default' | 'vi-VN';

/** localStorage key — must match the inline boot script in `app.html`, which sets `lang` before first paint. */
export const CULTURE_STORAGE_KEY = 'voicedojo:culture';

/** Maps a culture token to the BCP-47 tag for the document `lang` attribute. */
export function langTag(culture: Culture): string {
  return culture === 'vi-VN' ? 'vi-VN' : 'en';
}

/** The culture name the backend stores and renders emails in. */
export function wireCulture(culture: Culture): string {
  return culture === 'default' ? DefaultCulture : culture;
}

function readInitial(): Culture {
  if (!browser) return 'default';
  try {
    const stored = localStorage.getItem(CULTURE_STORAGE_KEY);
    if (stored === 'vi-VN' || stored === 'default') return stored;
  } catch {
    // storage disabled — fall through to the default culture.
  }
  return 'default';
}

let current = $state<Culture>(readInitial());

/** The active culture (reactive). */
export function activeCulture(): Culture {
  return current;
}

/** Switches the active culture, persisting it and updating `<html lang>`. */
export function setCulture(culture: Culture): void {
  current = culture;
  if (!browser) return;
  document.documentElement.setAttribute('lang', langTag(culture));
  try {
    localStorage.setItem(CULTURE_STORAGE_KEY, culture);
  } catch {
    // Private-mode / storage-disabled — the attribute still applies for the
    // session, we just can't persist it. Non-fatal.
  }
}
